import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { base_url } from '../../utils/config';
import {
  AiFillHeart,
  AiOutlineHeart,
  AiOutlineShoppingCart
} from 'react-icons/ai';
import { FaFemale } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Ratings from '../Ratings';
import {
  add_to_card,
  add_to_wishlist,
  remove_wishlist,
  get_wishlist_products,
  messageClear
} from '../../store/reducers/cardReducer';
import { 
  get_reviews,
  add_viewed_product,
  add_cart_activity,
  add_wishlist_activity,
  add_swap_activity
} from '../../store/reducers/homeReducer';
import { addSwapItem } from '../../store/reducers/virtualSwapReducer';
import '../../styles/commonfeminine.css';

// compute average rating
const getAverageRating = (product) => {
  if (!product?.rating_review?.length) return 0;
  let total = 0, sum = 0;
  product.rating_review.forEach((rev, i) => {
    const c = rev.sum || 0, val = 5 - i;
    total += c;
    sum += c * val;
  });
  return total ? (sum / total).toFixed(1) : 0;
};

// merge in per‑product reviews
const fetchReviewsForProducts = async (products, dispatch) => {
  const updated = [];
  for (const p of products) {
    try {
      const res = await dispatch(get_reviews({ productId: p._id, pageNumber: 1 })).unwrap();
      updated.push({
        ...p,
        rating_review: res.rating_review || [],
        totalReview: res.totalReview || 0,
      });
    } catch {
      updated.push({ ...p, rating_review: [], totalReview: 0 });
    }
  }
  return updated;
};

const TopRatedProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector(s => s.auth);
  const { wishlist, successMessage, errorMessage } = useSelector(s => s.card);
  const [products, setProducts] = useState([]);

  // load wishlist
  useEffect(() => {
    if (userInfo) dispatch(get_wishlist_products(userInfo.id));
  }, [dispatch, userInfo]);

  // fetch top‑rated + reviews
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${base_url}/api/customer/top-rated-products`);
        const initial = res.data.products || [];
        const merged  = await fetchReviewsForProducts(initial, dispatch);
        setProducts(merged);
      } catch (err) {
        console.error('Error loading top rated products:', err);
      }
    })();
  }, [dispatch]);

  // show toasts & refresh
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      if (userInfo) dispatch(get_wishlist_products(userInfo.id));
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch, userInfo]);

  const addCard = (id) => {
    if (!userInfo) return navigate('/login');
    dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
    // Track cart activity (removed duplicate trackAddToCart call)
    dispatch(add_cart_activity({ userId: userInfo.id, productId: id, source: 'TopRatedProducts' }));
  };

  const addSwap = (p, e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    dispatch(addSwapItem({
      userId: userInfo.id,
      productId: p._id,
      slug: p.slug,
      name: p.name,
      price: p.price,
      discount: p.discount,
      image: p.images[0],
      category: p.category,
      shopName: p.shopName,
    }));
    // Track swap activity
    dispatch(add_swap_activity({ userId: userInfo.id, productId: p._id, source: 'TopRatedProducts' }));
  };

  const toggleWishlist = (p, e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    const existing = wishlist.find(w => w.productId === p._id);
    if (existing) {
      dispatch(remove_wishlist(existing._id));
    } else {
      dispatch(add_to_wishlist({
        userId: userInfo.id,
        productId: p._id,
        name: p.name,
        price: p.price,
        image: p.images[0],
        discount: p.discount,
        slug: p.slug,
      }));
      // Track wishlist activity
      dispatch(add_wishlist_activity({ userId: userInfo.id, productId: p._id, source: 'TopRatedProducts' }));
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/details/${productId}`);
    // View tracking is handled in Details page to avoid duplicates
  };

  if (!products.length) {
    return (
      <div className="w-[85%] mx-auto py-10 text-center text-secondary">
        No top rated products found.
      </div>
    );
  }

  return (
    <div className="w-[85%] mx-auto py-10">
      {/* heading */}
      <div className="mb-8 flex justify-center">
        <h2
          className="text-2xl font-bold text-center"
          style={{
            color: 'var(--color-text-primary)',
            textDecoration: 'underline',
            textUnderlineOffset: '4px'
          }}
        >
          Top Rated Products
        </h2>
      </div>

      <div className="flex gap-6 overflow-x-auto hide-scrollbar pb-4">
        {products.map((p, i) => {
          const avg = getAverageRating(p);
          const discounted = p.discount
            ? p.price - Math.floor((p.price * p.discount) / 100)
            : p.price;
          const inWish = wishlist.some(w => w.productId === p._id);

          return (
            <div
              key={p._id || i}
              onClick={() => handleProductClick(p._id)}
              className="card min-w-[200px] max-w-[200px] flex-shrink-0 flex flex-col"
            >
              {/* image */}
              <div className="relative aspect-[3.4/4] overflow-hidden">
                <img
                  src={p.images[0]}
                  alt={p.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={e => toggleWishlist(p, e)}
                  className="absolute top-2 left-2 p-1 rounded-full"
                  style={{
                    background: 'var(--color-card-bg)',
                    border: `1px solid var(--color-border)`,
                    color: inWish ? 'var(--color-primary)' : 'var(--color-border)',
                  }}
                >
                  {inWish ? <AiFillHeart size={18} /> : <AiOutlineHeart size={18} />}
                </button>
                {p.discount > 0 && (
                  <div
                    className="absolute bottom-2 left-2 px-2 py-1 bg-white bg-opacity-80 rounded text-xs font-semibold"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    -{p.discount}%
                  </div>
                )}
              </div>

              {/* details */}
              <div className="p-3 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-base font-semibold line-clamp-1" style={{ color: 'var(--color-text-primary)' }}>
                    {p.name}
                  </h3>
                  {p.shopName && (
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {p.shopName}
                    </p>
                  )}
                  <div className="mt-2 flex items-baseline gap-2">
                    {p.discount > 0 ? (
                      <>
                        <span className="line-through text-sm text-gray-500">₹{p.price}</span>
                        <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                          ₹{discounted}
                        </span>
                      </>
                    ) : (
                      <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                        ₹{p.price}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-600">
                    <Ratings ratings={parseFloat(avg)} />
                    <span className="ml-2">({p.totalReview || 0})</span>
                  </div>
                </div>
              </div>

              {/* action pill */}
              <div
                className="flex h-12 w-full border-t rounded-b-lg overflow-hidden"
                style={{ borderColor: 'var(--color-border)' }}
              >
                {/* Add to Cart */}
                <button
                  onClick={e => { e.stopPropagation(); addCard(p._id); }}
                  className="button-primary btn-cart flex-1 flex flex-col items-center justify-center rounded-bl-lg"
                >
                  <span className="font-semibold text-sm">Add to Cart</span>
                  <AiOutlineShoppingCart className="text-xl mt-1 font-bold" />
                </button>

                {/* Virtual Swap */}
                <button
                  onClick={e => addSwap(p, e)}
                  className="button-secondary btn-swap flex-1 flex flex-col items-center justify-center rounded-br-lg border-l"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-primary)',      /* terracotta text */
                    background: 'var(--color-accent)',   /* keep your sage bg */
                  }}
                >
                  <span className="font-semibold text-sm">Virtual Swap</span>
                  <FaFemale className="text-xl mt-1 font-bold" />
                </button>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TopRatedProducts;
